import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, REGISTRY_STYLES, formatDate, fcraStateName } from "@/lib/api";
import { AsOfBar } from "@/components/PageHeader";
import { ConfidenceBadge } from "@/components/Badges";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ArrowLeft, Landmark, Users, FileText, Ban, CheckCircle2, XCircle, ExternalLink, Copy, AlertTriangle, Info, ShieldCheck, Loader2, Award, FileCheck2 } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { DONOR_SOURCE_NOTE } from "@/lib/api";

function Section({ icon: Icon, title, badge, children }) {
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5 backdrop-blur-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="flex items-center gap-2 text-base font-semibold text-slate-200">
          <Icon className="w-4 h-4 text-blue-400" /> {title}
        </h3>
        {badge}
      </div>
      {children}
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className={`text-sm font-medium text-slate-100 break-words ${mono ? "font-mono" : ""}`}>{value ?? "—"}</div>
    </div>
  );
}

const join = (parts) => parts.filter(Boolean).join(", ") || null;

const itAddress = (r) => r && join([
  r.flatDoorBuilding, r.nameOfPremisesBuildingVillage, r.roadStreetPostOffice,
  r.areaLocality, r.townCityDistrict, r.state, r.pincode, r.country,
]);

function IdChip({ registry, label, value }) {
  const r = REGISTRY_STYLES[registry] || { bg: "bg-slate-200 text-slate-700 border-slate-300" };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-mono ${r.bg}`}>
      <span className="font-semibold uppercase opacity-70">{label}</span>
      <span className="font-semibold">{value}</span>
    </span>
  );
}

export default function Profile() {
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [gst, setGst] = useState(null);
  const [gstLoading, setGstLoading] = useState(false);
  const [gstErr, setGstErr] = useState("");
  const [returnsOpen, setReturnsOpen] = useState(false);
  const [returnsGstin, setReturnsGstin] = useState(null);
  const [returnsFy, setReturnsFy] = useState("FY 2025-26");
  const [returnsData, setReturnsData] = useState(null);
  const [returnsLoading, setReturnsLoading] = useState(false);

  const entityKey = decodeURIComponent(location.pathname.replace("/app/ngo/", ""));

  useEffect(() => {
    api.get(`/v1/ngo/${encodeURIComponent(entityKey)}`)
      .then((r) => { setData(r.data); if (r.data.gst) setGst(r.data.gst); })
      .catch((e) => setErr(e.response?.data?.detail || "Not found"));
  }, [entityKey]);

  const runGstCheck = async () => {
    setGstLoading(true); setGstErr(""); setGst(null);
    try {
      const r = await api.post(`/v1/ngo/${encodeURIComponent(entityKey)}/gst-check`);
      setGst(r.data);
    } catch (e) {
      setGstErr(e.response?.data?.detail || "GST check failed. Please try again.");
    } finally {
      setGstLoading(false);
    }
  };

  const loadReturns = async (gstin, fy) => {
    setReturnsLoading(true); setReturnsData(null);
    try {
      const r = await api.post(`/v1/gst/returns`, { gstin, financial_year: fy });
      setReturnsData(r.data);
    } catch (e) {
      setReturnsData({ error: e.response?.data?.detail || "Failed to fetch returns." });
    } finally {
      setReturnsLoading(false);
    }
  };

  const openReturns = (gstin) => {
    setReturnsGstin(gstin); setReturnsOpen(true);
    loadReturns(gstin, returnsFy);
  };

  const changeFy = (fy) => { setReturnsFy(fy); loadReturns(returnsGstin, fy); };

  if (err) return <div className="text-rose-300">{err}</div>;
  if (!data) return <div className="font-mono text-xs text-slate-500">Loading profile…</div>;

  const fcraActive = data.fcra?.fcraStatus === "ACTIVE";
  const fcraNotActive = data.fcra && !fcraActive;
  const multiDarpan = (data.ids?.darpan?.length || 0) > 1;
  const warnings = [];
  if (fcraNotActive) warnings.push(`FCRA registration is not active (status: ${data.fcra.fcraStatus || "unknown"}).`);
  if (multiDarpan) warnings.push(`Multiple Darpan registrations (${data.ids.darpan.length}) found for this NGO.`);

  return (
    <div>
      <button
        onClick={() => navigate(-1)}
        className="mb-5 inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-200 transition-colors duration-150"
        data-testid="profile-back-btn"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="mb-8 animate-fade-up">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-100">{data.name}</h1>
            <p className="mt-1 text-sm text-slate-400">{data.state}</p>

            <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="header-id-chips">
              {data.ids.pan && <IdChip registry="incometax" label="PAN" value={data.ids.pan.value} />}
              {data.ids.darpan.map((d, i) => <IdChip key={d.value} registry="darpan" label="Darpan" value={d.value} />)}
              {data.ids.fcra && <IdChip registry="fcra" label="FCRA" value={data.ids.fcra.value} />}
              {data.ids.csr1 && <IdChip registry="csr1" label="CSR-1" value={data.ids.csr1.value} />}
            </div>

            {data.donors?.length > 0 && (
              <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3" data-testid="donors-inline">
                <p className="font-mono text-[10px] uppercase tracking-wider text-emerald-700 mb-1.5 flex items-center gap-1.5">
                  Donors · {data.donors.length}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" data-testid="donor-source-info" className="inline-flex text-emerald-600 hover:text-emerald-800 transition-colors">
                        <Info className="w-3.5 h-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">{DONOR_SOURCE_NOTE}</TooltipContent>
                  </Tooltip>
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {data.donors.map((d, i) => (
                    <span key={d} data-testid={`donor-chip-${i}`} className="rounded-md border border-emerald-300 bg-white px-2 py-1 text-xs font-semibold text-emerald-800">
                      {d}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="shrink-0 flex flex-col items-start sm:items-end gap-3">
            {data.overall_tier && <ConfidenceBadge info={data.overall_tier} />}
            {data.ids?.pan && (
              <button
                data-testid="gst-check-btn"
                onClick={runGstCheck}
                disabled={gstLoading}
                className="inline-flex items-center gap-2 rounded-full bg-blue-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-blue-400 disabled:opacity-60 transition-colors duration-150"
              >
                {gstLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                {gst ? "Re-check GST Compliance" : "Check GST Compliance"}
              </button>
            )}
          </div>
        </div>
      </div>

      {warnings.length > 0 && (
        <div data-testid="profile-warning-banner" className="mb-6 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <div className="flex items-center gap-2 font-semibold">
            <AlertTriangle className="w-4 h-4" /> Attention required
          </div>
          <ul className="mt-2 ml-6 list-disc space-y-1">
            {warnings.map((w, i) => <li key={w} data-testid={`profile-warning-${i}`}>{w}</li>)}
          </ul>
        </div>
      )}

      {(gst || gstErr || gstLoading) && (
        <div data-testid="gst-results" className="mb-6 rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="flex items-center gap-2 text-base font-semibold text-slate-200">
              <ShieldCheck className="w-4 h-4 text-blue-400" /> GST Compliance
            </h3>
            {gst?.checked_at && <span className="font-mono text-[11px] text-slate-500">checked {formatDate(gst.checked_at)}</span>}
          </div>
          {gstLoading ? (
            <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Checking GST registrations…</div>
          ) : gstErr ? (
            <div className="text-sm text-rose-700" data-testid="gst-error">{gstErr}</div>
          ) : gst?.gstins?.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left font-mono text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="py-2 pr-4">GSTIN</th>
                    <th className="py-2 pr-4">Legal name</th>
                    <th className="py-2 pr-4">State</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4">Registered</th>
                    <th className="py-2 pr-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {gst.gstins.map((g, i) => (
                    <tr key={g.gstin || i} data-testid={`gst-row-${i}`} className="border-t border-slate-800/60">
                      <td className="py-2 pr-4 font-mono text-xs text-slate-200">{g.gstin || "—"}</td>
                      <td className="py-2 pr-4 text-slate-300">{g.legal_name || g.trade_name || "—"}</td>
                      <td className="py-2 pr-4 text-slate-300">{g.state || "—"}</td>
                      <td className="py-2 pr-4">
                        <span className={`rounded-md border px-2 py-0.5 text-xs font-semibold ${g.status === "Active" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-rose-200 bg-rose-50 text-rose-700"}`}>
                          {g.status || "—"}
                        </span>
                      </td>
                      <td className="py-2 pr-4 font-mono text-xs text-slate-400">{formatDate(g.registration_date)}</td>
                      <td className="py-2 pr-4">
                        {g.status === "Active" && g.gstin && (
                          <button
                            data-testid={`verify-returns-btn-${i}`}
                            onClick={() => openReturns(g.gstin)}
                            className="inline-flex items-center gap-1.5 rounded-full border border-blue-500 px-3 py-1 text-xs font-semibold text-blue-600 hover:bg-blue-50 transition-colors duration-150"
                          >
                            <FileCheck2 className="w-3.5 h-3.5" /> Verify Returns
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-sm text-slate-500" data-testid="gst-empty">{gst?.message || "No GST records found for this PAN."}</div>
          )}
        </div>
      )}

      {data.blacklist_hits?.length > 0 && (
        <div data-testid="blacklist-alert" className="mb-6 flex items-center gap-2 rounded-xl border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          <Ban className="w-4 h-4" /> Flagged on a debarred / blacklist source: {data.blacklist_hits.map((b) => b.source).join(", ")}
        </div>
      )}

      <div className="mb-6"><AsOfBar asOf={data.as_of} /></div>

      <div className="grid gap-5 lg:grid-cols-2">
        {data.guidestar && (
          <div className="lg:col-span-2">
            <Section icon={Award} title="GuideStar India"
              badge={<span className="rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-mono text-amber-800">{data.guidestar.certification_level}</span>}>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                <Field label="Certification level" value={data.guidestar.certification_level} />
                <Field label="GuideStar rating" value={data.guidestar.guidestar_rating} />
                <Field label="Valid till" value={formatDate(data.guidestar.certification_valid_till)} />
                <Field label="Currently valid" value={data.guidestar.certification_current ? "Yes" : "No"} />
                <Field label="GSI number" value={data.guidestar.gsi_number} mono />
              </div>
              {data.guidestar.budgets && Object.keys(data.guidestar.budgets).length > 0 && (
                <div className="mt-4">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-slate-400 mb-2">Annual budget (₹)</div>
                  <div className="flex flex-wrap gap-2" data-testid="guidestar-budgets">
                    {Object.entries(data.guidestar.budgets).sort((a, b) => b[0].localeCompare(a[0])).map(([fy, amt]) => (
                      <div key={fy} className="rounded-lg border border-slate-800 bg-slate-950/40 px-3 py-2">
                        <div className="font-mono text-[10px] text-slate-500">{fy.replace("FY", "FY ")}</div>
                        <div className="text-sm font-semibold text-slate-100">₹{Number(amt).toLocaleString("en-IN")}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {data.guidestar.guidestar_url && (
                <a href={data.guidestar.guidestar_url} target="_blank" rel="noopener noreferrer"
                  data-testid="guidestar-link"
                  className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-500 transition-colors">
                  View on GuideStar India <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </Section>
          </div>
        )}
        {/* Income Tax */}
        <Section icon={FileText} title="Income Tax · 12A / 80G" badge={<span className={`rounded-md border px-2 py-0.5 text-[10px] font-mono ${REGISTRY_STYLES.incometax.bg}`}>{data.ids.pan?.value}</span>}>
          {data.income_tax?.length ? (
            <div className="space-y-3">
              <Field label="Registered address" value={itAddress(data.income_tax[0])} />
              <div className="grid grid-cols-3 gap-3">
                <Field label="City / District" value={data.income_tax[0].townCityDistrict} />
                <Field label="State" value={data.income_tax[0].state} />
                <Field label="Pincode" value={data.income_tax[0].pincode} mono />
              </div>
              {data.income_tax.map((r, i) => (
                <div key={i} className="rounded-lg border border-slate-800 p-3">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs font-semibold text-sky-700">{r.sectionCode || "—"}</span>
                    <span className={`rounded px-2 py-0.5 text-[10px] font-mono ${r.exemptedStatus === "Active" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>{r.exemptedStatus}</span>
                  </div>
                  {r.sectionNumber && <p className="mt-1 text-[11px] text-slate-400 break-words">{r.sectionNumber}</p>}
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <Field label="URN" value={r.uniqueRegistrationNumber} mono />
                    <Field label="Registered" value={formatDate(r.dateOfRegistration)} />
                    <Field label="A.Y. from" value={r.ayDateFromRegistered} mono />
                    {r.dateOfSurrender && <Field label="Surrendered" value={formatDate(r.dateOfSurrender)} />}
                  </div>
                </div>
              ))}
              <div className="rounded-lg border border-sky-200 bg-sky-50 p-3">
                <p className="text-[11px] leading-relaxed text-sky-800">
                  Registration details reflect our last Income Tax sync. To see the live record, open the CBDT registered non-profit search and look up the PAN.
                </p>
                <div className="mt-2.5 flex flex-wrap items-center gap-2">
                  <code data-testid="it-pan-chip" className="rounded-md border border-sky-300 bg-white px-2 py-1 font-mono text-xs font-semibold text-slate-100">{data.ids.pan?.value}</code>
                  <button
                    onClick={() => { navigator.clipboard?.writeText(data.ids.pan?.value); toast.success("PAN copied"); }}
                    data-testid="it-copy-btn"
                    className="inline-flex items-center gap-1.5 rounded-full border border-sky-300 px-3 py-1.5 text-xs font-semibold text-sky-800 hover:bg-sky-100 transition-colors duration-150"
                  >
                    <Copy className="w-3.5 h-3.5" /> Copy PAN
                  </button>
                  <a
                    href="https://www.incometaxindia.gov.in/registered-non-profit-organizations"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="it-verify-link"
                    className="inline-flex items-center gap-1.5 rounded-full bg-blue-500 px-3.5 py-1.5 text-xs font-semibold text-slate-950 hover:bg-blue-400 transition-colors duration-150"
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> Open CBDT portal
                  </a>
                </div>
              </div>
            </div>
          ) : <div className="text-sm text-slate-500">No IT registrations.</div>}
        </Section>

        {/* FCRA */}
        <Section icon={Landmark} title="FCRA · Ministry of Home Affairs"
          badge={data.fcra && <span className={`rounded-md border px-2 py-0.5 text-[10px] font-mono ${REGISTRY_STYLES.fcra.bg}`}>{data.fcra.fcraRegistrationNumber}</span>}>
          {data.fcra ? (
            <div>
              <div className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold ${fcraActive ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
                {fcraActive ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />} {data.fcra.fcraStatus}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <Field label="Association" value={data.fcra.associationName} />
                <Field label="Nature" value={data.fcra.nature} />
                <Field label="State" value={data.fcra.sourceState || (data.fcra.stateCode ? fcraStateName(data.fcra.stateCode) : "—")} />
                <Field label="Registration year" value={data.fcra.registrationYear} mono />
                {fcraActive && <Field label="Valid from" value={formatDate(data.fcra.validityFromDate)} />}
                {fcraActive && <Field label="Valid to" value={formatDate(data.fcra.validityToDateISO)} mono />}
                {fcraActive && <Field label="Last renewed" value={formatDate(data.fcra.lastRenewedDate)} />}
              </div>
              <div className="mt-3"><Field label="Address" value={data.fcra.associationAddress} /></div>
              {!fcraActive && <p className="mt-3 text-[11px] text-amber-600">Validity hidden — status is the source of truth, not the date.</p>}
              <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="text-[11px] leading-relaxed text-amber-800">
                  Validity dates sit behind a CAPTCHA on the MHA portal, so they reflect our last crawl.
                  To confirm the live certificate, open the validator and paste the FCRA number, then solve the CAPTCHA.
                </p>
                <div className="mt-2.5 flex flex-wrap items-center gap-2">
                  <code data-testid="fcra-number-chip" className="rounded-md border border-amber-300 bg-white px-2 py-1 font-mono text-xs font-semibold text-slate-100">{data.fcra.fcraRegistrationNumber}</code>
                  <button
                    onClick={() => { navigator.clipboard?.writeText(data.fcra.fcraRegistrationNumber); toast.success("FCRA number copied"); }}
                    data-testid="fcra-copy-btn"
                    className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100 transition-colors duration-150"
                  >
                    <Copy className="w-3.5 h-3.5" /> Copy number
                  </button>
                  <a
                    href="https://fcraonline.gov.in/validate-certificate"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="fcra-verify-link"
                    className="inline-flex items-center gap-1.5 rounded-full bg-blue-500 px-3.5 py-1.5 text-xs font-semibold text-slate-950 hover:bg-blue-400 transition-colors duration-150"
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> Verify on fcraonline.gov.in
                  </a>
                </div>
              </div>
            </div>
          ) : <div className="text-sm text-slate-500">No linked FCRA registration.</div>}
        </Section>

        {/* Darpan */}
        <Section icon={Users} title="NGO Darpan · NITI Aayog"
          badge={data.darpan?.[0] && <span className={`rounded-md border px-2 py-0.5 text-[10px] font-mono ${REGISTRY_STYLES.darpan.bg}`}>{data.darpan[0].darpanId}</span>}>
          {data.darpan?.length ? data.darpan.map((d, i) => (
            <div key={i} className={i > 0 ? "mt-6 pt-6 border-t border-slate-800" : ""}>
              {data.darpan.length > 1 && (
                <div className="mb-3 flex flex-wrap items-center gap-2 font-mono text-[10px] uppercase tracking-wider">
                  <span className="rounded-md border border-purple-200 bg-purple-50 px-2 py-0.5 text-purple-700">Darpan registration {i + 1} of {data.darpan.length}</span>
                  <span className="text-slate-400">{d.darpanId}</span>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 mb-3">
                <Field label="Name" value={d.ngoName} />
                <Field label="Type" value={d.ngoType} />
                <Field label="Reg. No / CIN" value={d.registrationNo} mono />
                <Field label="Registered" value={formatDate(d.registrationDate)} />
                <Field label="Reg. authority" value={d.regAuthority} />
                <Field label="Act" value={d.actName} />
              </div>
              <div className="mb-3"><Field label="Address" value={join([d.address, d.subDistrictName, d.districtName, d.stateName, d.pincode])} /></div>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <Field label="Email" value={d.emailDecrypt} />
                <Field label="Mobile" value={d.mobileDecrypt} mono />
                {d.ngoUrl ? <Field label="Website" value={d.ngoUrl} /> : null}
              </div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-slate-400 mb-2">Office bearers</div>
              <div className="space-y-1.5">
                {d.members?.map((m, j) => (
                  <div key={j} className="flex items-center justify-between gap-3 rounded-lg border border-slate-800 bg-slate-900 px-3 py-2 text-sm">
                    <span className="min-w-0 truncate font-medium text-slate-100">{m.name || "—"}</span>
                    <span className="shrink-0 font-mono text-[11px] text-slate-400">{m.designation}</span>
                  </div>
                ))}
              </div>
            </div>
          )) : <div className="text-sm text-slate-500">Not found on NGO Darpan.</div>}
        </Section>

        {/* Linked identities */}
        <Section icon={FileText} title="Linked identities & confidence">
          <div className="space-y-2.5">
            {data.ids.darpan.map((d, i) => (
              <div key={d.value} className="flex items-center justify-between rounded-lg border border-slate-800 px-3 py-2.5">
                <span className="font-mono text-xs text-purple-700">Darpan · {d.value}</span>
                {d.linked ? <ConfidenceBadge info={d} tier={d.tier} /> : <span className="text-[11px] text-amber-600">possible, not verified</span>}
              </div>
            ))}
            {data.ids.fcra && (
              <div className="flex items-center justify-between rounded-lg border border-slate-800 px-3 py-2.5">
                <span className="font-mono text-xs text-amber-700">FCRA · {data.ids.fcra.value}</span>
                {data.ids.fcra.linked ? <ConfidenceBadge info={data.ids.fcra} tier={data.ids.fcra.tier} /> : <span className="text-[11px] text-amber-600">possible, not verified</span>}
              </div>
            )}
            {data.ids.csr1 && (
              <div className="flex items-center justify-between rounded-lg border border-slate-800 px-3 py-2.5">
                <span className="font-mono text-xs text-emerald-700">CSR-1 · {data.ids.csr1.value}</span>
              </div>
            )}
          </div>
        </Section>
      </div>

      <Sheet open={returnsOpen} onOpenChange={setReturnsOpen}>
        <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="gst-returns-panel">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2"><FileCheck2 className="w-4 h-4 text-blue-500" /> GST Returns</SheetTitle>
          </SheetHeader>
          <div className="mt-4">
            <div className="font-mono text-xs text-slate-500 mb-3">{returnsGstin}</div>
            <div className="flex flex-wrap gap-1.5 mb-4">
              {["FY 2025-26", "FY 2024-25", "FY 2023-24", "FY 2022-23"].map((fy) => (
                <button
                  key={fy}
                  data-testid={`returns-fy-${fy.replace(/\s/g, "")}`}
                  onClick={() => changeFy(fy)}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors duration-150 ${returnsFy === fy ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-300 text-slate-500 hover:text-slate-800"}`}
                >
                  {fy}
                </button>
              ))}
            </div>

            {returnsLoading ? (
              <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Fetching returns…</div>
            ) : returnsData?.error ? (
              <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="returns-error">{returnsData.error}</div>
            ) : returnsData ? (
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  <span className="rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">Filed: {returnsData.counts?.filed ?? 0}</span>
                  <span className={`rounded-md border px-2.5 py-1 text-xs font-semibold ${returnsData.counts?.late ? "border-amber-300 bg-amber-50 text-amber-800" : "border-slate-200 text-slate-500"}`}>Late: {returnsData.counts?.late ?? 0}</span>
                  <span className={`rounded-md border px-2.5 py-1 text-xs font-semibold ${returnsData.counts?.not_filed ? "border-rose-300 bg-rose-50 text-rose-700" : "border-slate-200 text-slate-500"}`}>Not filed: {returnsData.counts?.not_filed ?? 0}</span>
                </div>

                {returnsData.missing?.length > 0 && (
                  <div className="rounded-lg border border-rose-200 bg-rose-50 p-3" data-testid="returns-missing">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-rose-700 mb-2"><AlertTriangle className="w-3.5 h-3.5" /> Not filed ({returnsData.missing.length})</div>
                    <div className="flex flex-wrap gap-1.5">
                      {returnsData.missing.map((m, i) => (
                        <span key={`${m.rtntype}-${m.ret_prd}`} className="rounded border border-rose-200 bg-white px-2 py-0.5 text-[11px] font-mono text-rose-700">{m.rtntype} · {m.period_label}</span>
                      ))}
                    </div>
                  </div>
                )}

                {returnsData.returns?.length ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left font-mono text-[10px] uppercase tracking-wider text-slate-500">
                          <th className="py-2 pr-3">Type</th>
                          <th className="py-2 pr-3">Period</th>
                          <th className="py-2 pr-3">Filed on</th>
                          <th className="py-2 pr-3">Flag</th>
                        </tr>
                      </thead>
                      <tbody>
                        {returnsData.returns.map((r, i) => (
                          <tr key={r.arn || `${r.rtntype}-${r.ret_prd}`} data-testid={`return-row-${i}`} className={`border-t border-slate-100 ${r.late ? "bg-amber-50" : ""}`}>
                            <td className="py-2 pr-3 font-mono text-xs text-slate-700">{r.rtntype}</td>
                            <td className="py-2 pr-3 text-slate-700">{r.period_label}</td>
                            <td className="py-2 pr-3 font-mono text-xs text-slate-500">{r.dof || "—"}</td>
                            <td className="py-2 pr-3">
                              {r.late ? (
                                <span className="rounded-md border border-amber-300 bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">Late</span>
                              ) : (
                                <span className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">On time</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-sm text-slate-500">No filed returns found for {returnsFy}.</div>
                )}
              </div>
            ) : null}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
